import { useEffect, useState, useCallback } from "react";
import { useACLContext, type CheckOptions } from "../context";
import type { Action, Resource } from "@shield-acl/core";
import { stableKey } from "./internal";

export interface AsyncCheck {
  /** undefined enquanto carrega ou em erro. */
  allowed: boolean | undefined;
  loading: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Verificação assíncrona — destrava conditions async, `policySource` e ReBAC
 * na UI (ex.: consulta a banco).
 *
 * @example
 * const { allowed, loading } = useCanAsync("edit", "docs", { record: doc })
 * if (loading) return <Spinner />
 * return allowed ? <Editor /> : <Denied />
 */
export function useCanAsync(
  action: Action,
  resource?: Resource,
  opts?: CheckOptions,
): AsyncCheck {
  const { canAsync, user, revision } = useACLContext();
  const [state, setState] = useState<{
    allowed: boolean | undefined;
    loading: boolean;
    error: unknown;
  }>({ allowed: undefined, loading: true, error: null });
  const [nonce, setNonce] = useState(0);
  const refetch = useCallback(() => setNonce((n) => n + 1), []);
  const key = stableKey(opts);

  useEffect(() => {
    let alive = true;
    setState((s) => (s.loading ? s : { ...s, loading: true }));
    canAsync(action, resource, opts)
      .then((r) => {
        if (alive)
          setState({ allowed: r.allowed, loading: false, error: null });
      })
      .catch((e) => {
        if (alive) setState({ allowed: undefined, loading: false, error: e });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAsync, action, resource, key, user, revision, nonce]);

  return { ...state, refetch };
}
